/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import styled from "@emotion/styled";
import { useDeviceBasedTextStyles } from "hooks/useDeviceBasedTextStyles";
import { CSSProperties, ElementType, ReactElement, ReactNode } from "react";
import { Theme } from "../../styles/theme";

// eslint-disable-next-line @typescript-eslint/ban-types
type AllKeys<T> = T extends {} ? keyof T : never;
type Device = "Mobile" | "Desktop";
export type TextStyle = AllKeys<Theme[Device]>;
export type TextSize<T extends TextStyle> = keyof Theme[Device][T];

export interface TextProps<T extends TextStyle> {
  textStyle: T;
  size: TextSize<T>;
  children: ReactNode;
  style?: CSSProperties;
  textCss?: SerializedStyles;
  color?: string;
  as?: ElementType;
  isVisible?: boolean;
  shouldScale?: boolean;
}

export const Text: <T extends TextStyle>(
  props: TextProps<T>
) => ReactElement<TextProps<T>> | null = ({
  textStyle,
  size,
  children,
  textCss,
  color,
  as,
  style,
  isVisible = true,
  shouldScale = false,
}) => {
  const textStyles = useDeviceBasedTextStyles({ shouldScale });
  const el = textStyles[textStyle];
  const font = el[size] as string;

  if (!isVisible) {
    return null;
  }

  return (
    <ElementToRender
      as={as}
      style={style}
      css={css`
        font: ${font};
        color: ${color};
        margin: 0;
        ${textCss};
      `}
    >
      {children}
    </ElementToRender>
  );
};

// Sub-components

const ElementToRender = styled.div``;
